import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Chip,
} from '@material-tailwind/react';
import {
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

const YEAR_RANGES = [
  { value: 0, label: 'Exact year' },
  { value: 1, label: '± 1 year' },
  { value: 2, label: '± 2 years' },
  { value: 3, label: '± 3 years' },
  { value: 5, label: '± 5 years' },
  { value: -1, label: 'All years' },
];

const PERIOD_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 120, label: '120 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
  { value: 730, label: '2 years' },
  { value: 1095, label: '3 years' },
];

function AnalysisConfigPanel({ draft, onValuesChange, onDataChanged, resetKey }) {
  // Original values from draft (for change detection)
  const originalYearRange = draft?.analysisConfig?.yearRange ?? 0;
  const originalPeriodDays = draft?.analysisConfig?.periodDays ?? 90;

  // Form state
  const [yearRange, setYearRange] = useState(originalYearRange);
  const [periodDays, setPeriodDays] = useState(originalPeriodDays);

  // Reset form when switching to a different draft or when resetKey changes (discard)
  useEffect(() => {
    setYearRange(draft?.analysisConfig?.yearRange ?? 0);
    setPeriodDays(draft?.analysisConfig?.periodDays ?? 90);
  }, [draft?.analysisConfig?.periodDays, draft?.analysisConfig?.yearRange, draft.draftId, resetKey]);

  // Detect what changed
  const dataChanged = useMemo(() => {
    return (
      yearRange !== originalYearRange ||
      periodDays !== originalPeriodDays
    );
  }, [yearRange, periodDays, originalYearRange, originalPeriodDays]);

  // Notify parent of current values whenever they change
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange({
        yearRange,
        periodDays,
      });
    }
  }, [yearRange, periodDays, onValuesChange]);

  // Notify parent of change status
  useEffect(() => {
    if (onDataChanged) {
      onDataChanged(dataChanged);
    }
  }, [dataChanged, onDataChanged]);

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Cog6ToothIcon className="h-5 w-5 text-blue-500" />
          <Typography variant="h6" color="blue-gray">
            Analysis Config
          </Typography>
        </div>
        {dataChanged && (
          <Chip
            size="sm"
            value="Modified"
            color="amber"
            variant="ghost"
          />
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div>
          <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
            Year Range
          </Typography>
          <select
            value={String(yearRange)}
            onChange={(e) => setYearRange(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 border border-blue-gray-200 rounded-md focus:outline-none focus:border-blue-500 bg-white"
          >
            {YEAR_RANGES.map((r) => (
              <option key={String(r.value)} value={String(r.value)}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
            Analysis Period
          </Typography>
          <select
            value={String(periodDays)}
            onChange={(e) => setPeriodDays(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 border border-blue-gray-200 rounded-md focus:outline-none focus:border-blue-500 bg-white"
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.value} value={String(p.value)}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default AnalysisConfigPanel;
